import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import SqrtInput, { getSqrtInputNumericValue } from "../../../../components/inputs/SqrtInput";

const TASK_TYPES = [
  "AnalyticVectorFromPointsTask",
  "AnalyticVectorLengthTask",
  "AnalyticOppositeVectorTask",
  "AnalyticVectorArithmeticTask",
  "AnalyticVectorEndpointTask",
];

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const nonZeroInt = (min, max) => {
  let value = 0;
  while (value === 0) value = randInt(min, max);
  return value;
};

const fsNumber = (n) => (n < 0 ? `(${n})` : `${n}`);
const signed = (n) => (n < 0 ? `${n}` : `+${n}`);
const pointPairLatex = (x, y) => `\\left(${x},\\ ${y}\\right)`;
const vectorPairLatex = (x, y) => `\\left[${x},\\ ${y}\\right]`;
const pointLatex = (name, x, y) => `${name}=(${x},\\ ${y})`;
const vectorLatex = (name, x, y) => `\\vec{${name}}=${vectorPairLatex(x, y)}`;
const abVectorLatex = String.raw`\overrightarrow{AB}`;
const vectorLengthFormulaLatex = String.raw`|\vec{u}|=\sqrt{u_x^2+u_y^2}`;

function randomPoint() {
  return { x: randInt(-6, 6), y: randInt(-6, 6) };
}

function randomVector() {
  return { x: nonZeroInt(-7, 7), y: nonZeroInt(-7, 7) };
}

function simplifySqrt(n) {
  let factor = 1;
  for (let i = Math.floor(Math.sqrt(n)); i >= 2; i -= 1) {
    if (n % (i * i) === 0) {
      factor = i;
      break;
    }
  }
  const outside = factor;
  const inside = n / (factor * factor);

  if (inside === 1) return `${outside}`;
  if (outside === 1) return `\\sqrt{${inside}}`;
  return `${outside}\\sqrt{${inside}}`;
}

function coerceRaw(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "object" && "raw" in value) return String(value.raw ?? "");
  if (typeof value === "object" && "latex" in value) return String(value.latex ?? "");
  return "";
}

function parseNumeric(raw) {
  if (raw && typeof raw === "object" && Number.isFinite(raw.parsed)) return raw.parsed;
  const cleaned = coerceRaw(raw).trim().replace(",", ".");
  if (cleaned === "") return NaN;
  return Number(cleaned);
}

function previewCoordinate(value) {
  if (value && typeof value === "object" && value.latex) return value.latex;
  const raw = coerceRaw(value).trim();
  return raw || "\\square";
}

function pairValueArray(value) {
  if (Array.isArray(value)) return [value[0] ?? "", value[1] ?? ""];
  if (value && typeof value === "object") {
    if ("x" in value || "y" in value) return [value.x ?? "", value.y ?? ""];
    if ("first" in value || "second" in value) return [value.first ?? "", value.second ?? ""];
  }
  return ["", ""];
}

function writePairSlot(value, index, nextSlot) {
  const current = pairValueArray(value);
  return index === 0 ? [nextSlot, current[1] ?? ""] : [current[0] ?? "", nextSlot];
}

function vectorInputLabels(task) {
  if (task?.type === "AnalyticVectorFromPointsTask") {
    return ["\\overrightarrow{AB}_x", "\\overrightarrow{AB}_y"];
  }
  if (task?.type === "AnalyticOppositeVectorTask") {
    return ["(-\\vec{u})_x", "(-\\vec{u})_y"];
  }
  if (task?.type === "AnalyticVectorArithmeticTask") {
    const symbol = task.data?.operation === "sum" ? "+" : "-";
    return ["\\left(\\vec{u}" + symbol + "\\vec{v}\\right)_x", "\\left(\\vec{u}" + symbol + "\\vec{v}\\right)_y"];
  }
  return ["\\vec{w}_x", "\\vec{w}_y"];
}

function vectorPreviewPrefix(task) {
  if (task?.type === "AnalyticVectorFromPointsTask") return abVectorLatex;
  if (task?.type === "AnalyticOppositeVectorTask") return "-\\vec{u}";
  if (task?.type === "AnalyticVectorArithmeticTask") {
    const symbol = task.data?.operation === "sum" ? "+" : "-";
    return "\\vec{u}" + symbol + "\\vec{v}";
  }
  return "\\vec{w}";
}

function renderCoordinateRows({ value, onChange, disabled, labels, previewMath, previewPrefix }) {
  const current = pairValueArray(value);
  const hasPreview = current[0] || current[1];
  const labelStyle = {
    display: "block",
    fontSize: "1.05rem",
    lineHeight: 1.15,
    marginBottom: 4,
  };
  const pairMath = previewMath(previewCoordinate(current[0]), previewCoordinate(current[1]));

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {[0, 1].map((index) => (
          <div key={index}>
            <span style={labelStyle}><InlineMath math={`${labels[index]} =`} /></span>
            <FractionInput
              value={current[index]}
              onChange={(next) => onChange?.(writePairSlot(value, index, next))}
              disabled={disabled}
              label={index === 0 ? "x" : "y"}
              placeholder="np. 3/4, 1 1/2, 0.75"
              showPreview={false}
            />
          </div>
        ))}
      </div>
      {hasPreview ? (
        <div style={{ marginTop: 8 }}>
          <strong>{"Podgl\u0105d: "}</strong>
          <InlineMath math={previewPrefix ? `${previewPrefix}=${pairMath}` : pairMath} />
        </div>
      ) : null}
    </div>
  );
}
function parsePairAnswer(value) {
  if (Array.isArray(value)) {
    return { x: parseNumeric(value[0]), y: parseNumeric(value[1]) };
  }
  if (value && typeof value === "object") {
    if ("x" in value && "y" in value) return { x: parseNumeric(value.x), y: parseNumeric(value.y) };
    if ("raw" in value) return parsePairAnswer(value.raw);
  }

  const raw = String(value ?? "")
    .replace(/[()[\]]/g, "")
    .replace(/;/g, ",")
    .trim();
  const parts = raw.split(",").map((part) => part.trim()).filter(Boolean);
  if (parts.length !== 2) return { x: NaN, y: NaN };
  return { x: parseNumeric(parts[0]), y: parseNumeric(parts[1]) };
}

function samePair(answer, expected) {
  const parsed = parsePairAnswer(answer);
  return parsed.x === expected.x && parsed.y === expected.y;
}

export function evaluateSqrtExpression(raw) {
  const parsed = getSqrtInputNumericValue(raw);
  return Number.isFinite(parsed) ? parsed : NaN;
}

function renderPairInput(task, userAnswer, onChange, disabled) {
  return renderCoordinateRows({
    value: userAnswer,
    onChange,
    disabled,
    labels: vectorInputLabels(task),
    previewMath: vectorPairLatex,
    previewPrefix: vectorPreviewPrefix(task),
  });
}

function renderPointInput(_task, userAnswer, onChange, disabled) {
  return renderCoordinateRows({
    value: userAnswer,
    onChange,
    disabled,
    labels: ["x_B", "y_B"],
    previewMath: pointPairLatex,
    previewPrefix: "B",
  });
}

function renderLengthInput(_task, userAnswer, onChange, disabled) {
  return (
    <div style={{ marginTop: 14 }}>
      <SqrtInput value={coerceRaw(userAnswer)} onChange={onChange} disabled={disabled} />
    </div>
  );
}

function renderPairAnswer(expected) {
  return <InlineMath math={vectorPairLatex(expected.x, expected.y)} />;
}

function renderPairUserAnswer(userAnswer) {
  const parsed = parsePairAnswer(userAnswer);
  if (!Number.isFinite(parsed.x) || !Number.isFinite(parsed.y)) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={vectorPairLatex(parsed.x, parsed.y)} />;
}

function renderPointAnswer(expected) {
  return <InlineMath math={pointPairLatex(expected.x, expected.y)} />;
}

function renderPointUserAnswer(userAnswer) {
  const parsed = parsePairAnswer(userAnswer);
  if (!Number.isFinite(parsed.x) || !Number.isFinite(parsed.y)) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={pointPairLatex(parsed.x, parsed.y)} />;
}

function renderLengthUserAnswer(userAnswer) {
  const raw = coerceRaw(userAnswer).trim();
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={raw} />;
}

export const registry = {
  AnalyticVectorFromPointsTask: {
    generate() {
      let A;
      let B;
      do {
        A = randomPoint();
        B = randomPoint();
      } while (A.x === B.x && A.y === B.y);
      return {
        type: "AnalyticVectorFromPointsTask",
        data: { A, B, answer: { x: B.x - A.x, y: B.y - A.y } },
      };
    },
    renderTask({ data }) {
      return (
        <>
          <p>{"Wyznacz wsp\u00f3\u0142rz\u0119dne wektora "}<InlineMath math={abVectorLatex} />{"."}</p>
          <p>
            <InlineMath math={pointLatex("A", data.A.x, data.A.y)} />,{" "}
            <InlineMath math={pointLatex("B", data.B.x, data.B.y)} />
          </p>
        </>
      );
    },
    renderInput: renderPairInput,
    checkAnswer: ({ data }, userAnswer) => samePair(userAnswer, data.answer),
    renderAnswer: ({ data }) => renderPairAnswer(data.answer),
    renderUserAnswer: (userAnswer) => renderPairUserAnswer(userAnswer),
    renderExplanation({ data }) {
      const { A, B, answer } = data;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong>{" Wektor od punktu "}<InlineMath math="A" />{" do punktu "}<InlineMath math="B" />{" liczymy jako r\u00f3\u017cnic\u0119 wsp\u00f3\u0142rz\u0119dnych ko\u0144ca i pocz\u0105tku."}</p>
          <BlockMath math={`${abVectorLatex}=\\left[x_B-x_A,\\ y_B-y_A\\right]`} />
          <p><strong>Krok 2.</strong> Podstawiamy dane.</p>
          <BlockMath math={`${abVectorLatex}=\\left[${fsNumber(B.x)}-${fsNumber(A.x)},\\ ${fsNumber(B.y)}-${fsNumber(A.y)}\\right]`} />
          <BlockMath math={`${abVectorLatex}=${vectorPairLatex(answer.x, answer.y)}`} />
        </div>
      );
    },
  },

  AnalyticVectorLengthTask: {
    generate() {
      const vector = pick([
        { x: 3, y: 4 }, { x: 5, y: 12 }, { x: 6, y: 8 },
        { x: 2, y: 5 }, { x: -4, y: 7 }, { x: -6, y: -3 }, { x: 1, y: -8 },
      ]);
      const underRoot = vector.x ** 2 + vector.y ** 2;
      return { type: "AnalyticVectorLengthTask", data: { vector, underRoot, answer: simplifySqrt(underRoot) } };
    },
    renderTask({ data }) {
      return <p>{"Oblicz d\u0142ugo\u015b\u0107 wektora "}<InlineMath math={vectorLatex("u", data.vector.x, data.vector.y)} />{"."}</p>;
    },
    renderInput: renderLengthInput,
    checkAnswer({ data }, userAnswer) {
      const expected = evaluateSqrtExpression(data.answer);
      const given = evaluateSqrtExpression(userAnswer);
      return Number.isFinite(given) && Math.abs(given - expected) < 0.01;
    },
    renderAnswer: ({ data }) => <InlineMath math={data.answer} />,
    renderUserAnswer: (userAnswer) => renderLengthUserAnswer(userAnswer),
    renderExplanation({ data }) {
      const { vector, underRoot, answer } = data;
      const simplification = answer === `\\sqrt{${underRoot}}` ? "" : `=${answer}`;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong>{" D\u0142ugo\u015b\u0107 wektora liczymy tak samo jak odleg\u0142o\u015b\u0107 punkt\u00f3w."}</p>
          <BlockMath math={vectorLengthFormulaLatex} />
          <p><strong>Krok 2.</strong>{" Podstawiamy sk\u0142adowe wektora."}</p>
          <BlockMath math={`|\\vec{u}|=\\sqrt{${fsNumber(vector.x)}^2+${fsNumber(vector.y)}^2}`} />
          <BlockMath math={`|\\vec{u}|=\\sqrt{${underRoot}}${simplification}`} />
        </div>
      );
    },
  },

  AnalyticOppositeVectorTask: {
    generate() {
      const vector = randomVector();
      return { type: "AnalyticOppositeVectorTask", data: { vector, answer: { x: -vector.x, y: -vector.y } } };
    },
    renderTask({ data }) {
      return <p>Wyznacz wektor przeciwny do wektora <InlineMath math={vectorLatex("u", data.vector.x, data.vector.y)} />.</p>;
    },
    renderInput: renderPairInput,
    checkAnswer: ({ data }, userAnswer) => samePair(userAnswer, data.answer),
    renderAnswer: ({ data }) => renderPairAnswer(data.answer),
    renderUserAnswer: (userAnswer) => renderPairUserAnswer(userAnswer),
    renderExplanation({ data }) {
      const { vector, answer } = data;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong>{" Wektor przeciwny ma sk\u0142adowe z przeciwnymi znakami."}</p>
          <BlockMath math={`-\\vec{u}=-${vectorPairLatex(vector.x, vector.y)}=${vectorPairLatex(-vector.x, -vector.y)}`} />
          <BlockMath math={`-\\vec{u}=${vectorPairLatex(answer.x, answer.y)}`} />
        </div>
      );
    },
  },

  AnalyticVectorArithmeticTask: {
    generate() {
      const u = randomVector();
      const v = randomVector();
      const operation = pick(["sum", "difference"]);
      const answer = operation === "sum" ? { x: u.x + v.x, y: u.y + v.y } : { x: u.x - v.x, y: u.y - v.y };
      return { type: "AnalyticVectorArithmeticTask", data: { u, v, operation, answer } };
    },
    renderTask({ data }) {
      const symbol = data.operation === "sum" ? "+" : "-";
      return (
        <p>
          {"Dane s\u0105 wektory "}<InlineMath math={vectorLatex("u", data.u.x, data.u.y)} />{" oraz "}
          <InlineMath math={vectorLatex("v", data.v.x, data.v.y)} />. Oblicz{" "}
          <InlineMath math={`\\vec{u}${symbol}\\vec{v}`} />.
        </p>
      );
    },
    renderInput: renderPairInput,
    checkAnswer: ({ data }, userAnswer) => samePair(userAnswer, data.answer),
    renderAnswer: ({ data }) => renderPairAnswer(data.answer),
    renderUserAnswer: (userAnswer) => renderPairUserAnswer(userAnswer),
    renderExplanation({ data }) {
      const { u, v, operation, answer } = data;
      const symbol = operation === "sum" ? "+" : "-";
      const secondX = symbol === "+" ? signed(v.x) : signed(-v.x);
      const secondY = symbol === "+" ? signed(v.y) : signed(-v.y);
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong>{" Wektory dodajemy albo odejmujemy po wsp\u00f3\u0142rz\u0119dnych."}</p>
          <BlockMath math={`\\vec{u}${symbol}\\vec{v}=${vectorPairLatex(u.x, u.y)}${symbol}${vectorPairLatex(v.x, v.y)}`} />
          <BlockMath math={`\\vec{u}${symbol}\\vec{v}=\\left[${u.x}${secondX},\\ ${u.y}${secondY}\\right]=${vectorPairLatex(answer.x, answer.y)}`} />
        </div>
      );
    },
  },

  AnalyticVectorEndpointTask: {
    generate() {
      const A = randomPoint();
      const vector = randomVector();
      return { type: "AnalyticVectorEndpointTask", data: { A, vector, answer: { x: A.x + vector.x, y: A.y + vector.y } } };
    },
    renderTask({ data }) {
      return (
        <p>
          {"Punkt "}<InlineMath math={pointLatex("A", data.A.x, data.A.y)} />{" jest pocz\u0105tkiem wektora "}
          <InlineMath math={`${abVectorLatex}=${vectorPairLatex(data.vector.x, data.vector.y)}`} />{". Wyznacz wsp\u00f3\u0142rz\u0119dne punktu "}<InlineMath math="B" />{"."}
        </p>
      );
    },
    renderInput: renderPointInput,
    checkAnswer: ({ data }, userAnswer) => samePair(userAnswer, data.answer),
    renderAnswer: ({ data }) => renderPointAnswer(data.answer),
    renderUserAnswer: (userAnswer) => renderPointUserAnswer(userAnswer),
    renderExplanation({ data }) {
      const { A, vector, answer } = data;
      return (
        <div style={{ textAlign: "left", lineHeight: 1.7 }}>
          <p><strong>Krok 1.</strong>{" Sk\u0142adowe wektora m\u00f3wi\u0105, o ile przesuwamy punkt pocz\u0105tkowy."}</p>
          <BlockMath math={`B=(x_A+${fsNumber(vector.x)},\\ y_A+${fsNumber(vector.y)})`} />
          <p><strong>Krok 2.</strong>{" Podstawiamy wsp\u00f3\u0142rz\u0119dne punktu "}<InlineMath math="A" />{"."}</p>
          <BlockMath math={`B=(${fsNumber(A.x)}+${fsNumber(vector.x)},\\ ${fsNumber(A.y)}+${fsNumber(vector.y)})=${pointPairLatex(answer.x, answer.y)}`} />
        </div>
      );
    },
  },
};

function moduleFor(task) {
  const module = registry[task?.type];
  if (!module) throw new Error(`Nieznany typ zadania z wektor\u00f3w: ${task?.type}`);
  return module;
}

export function generateTask(type = pick(TASK_TYPES)) {
  return registry[type].generate();
}

export function generateTasks() {
  return TASK_TYPES.map((type) => generateTask(type));
}

export function renderTask(task) {
  return moduleFor(task).renderTask(task);
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return moduleFor(task).renderInput(task, userAnswer, onChange, disabled);
}

export function checkAnswer(task, userAnswer) {
  return moduleFor(task).checkAnswer(task, userAnswer);
}

export function renderAnswer(task) {
  return moduleFor(task).renderAnswer(task);
}

export function renderUserAnswer(task, userAnswer) {
  return moduleFor(task).renderUserAnswer?.(userAnswer) ?? renderAnswer(task);
}

export function renderExplanation(task) {
  return moduleFor(task).renderExplanation(task);
}

export const vectorRegistry = registry;


